import { type FormFieldGroupValue, type FormFieldsSectionValue } from '@components/form-fields-section';
import { type ValidationError } from '@components/team-channel-section';
import { type ActiveChannelRequest } from '@domain/channel';
import { type Channel as EventTeamChannel, type EventTeam } from '@domain/eventTeam';
import { type FormFieldPreset } from '@domain/form-field-presets';

import { mapHolderGroupFromBackend } from './mappers/form-fields';
import { type ChannelSeedSource, seedChannelLimit } from './mappers/from-backend';
import { type AdmissionFormValue } from './admission-form.type';
import { type AdmissionFormErrors, isAdmissionFormValid } from './validation';

export const ACCESS_CODE_PATTERN = /^\w*$/;

export const hasIncompleteNumericField = (customFields: FormFieldGroupValue['customFields']): boolean =>
  customFields.some(
    (field) => field.value.type === 'number' && (field.value.min.trim() === '' || field.value.max.trim() === '')
  );

export const hasUnnamedCustomField = (customFields: FormFieldGroupValue['customFields']): boolean =>
  customFields.some((field) => field.value.name.trim() === '');

// Flat channels carry no team structure; ride them as `channelsWithoutTeams` to reuse the team machinery.
export const toEventTeamChannel = (channel: { id: string; name: string; slug: string }): EventTeamChannel => ({
  channel: { id: channel.id, name: channel.name, slug: channel.slug },
  isEnabled: true,
  team: null,
  maxTicketQuantity: null,
  isTicketSalesClosed: false,
  soldItems: { tickets: null, guestLists: null, bottleServices: null, passes: null },
});

export const isOfferSeedPending = (
  formFieldPresetsData: unknown,
  channelTeam: EventTeam | undefined,
  offer: { id?: string } | null | undefined,
  activeChannels: ActiveChannelRequest | undefined
): boolean => !formFieldPresetsData || !channelTeam || (!!offer?.id && !activeChannels);

export const isOfferSaveDisabled = (params: {
  isSeedPending: boolean;
  allErrors: AdmissionFormErrors;
  channelErrors?: ValidationError[];
  channelLimitError?: string;
  accessCodeError: boolean;
  formFields: AdmissionFormValue['formFields'];
}): boolean =>
  params.isSeedPending ||
  !isAdmissionFormValid(params.allErrors) ||
  (params.channelErrors?.length ?? 0) > 0 ||
  !!params.channelLimitError ||
  params.accessCodeError ||
  hasIncompleteNumericField(params.formFields.mainHolder.customFields) ||
  hasUnnamedCustomField(params.formFields.mainHolder.customFields) ||
  (params.formFields.isNominative &&
    (hasIncompleteNumericField(params.formFields.ticketHolder.customFields) ||
      hasUnnamedCustomField(params.formFields.ticketHolder.customFields)));

/** Minimal offer shape the channel + form-fields baseline read — satisfied by both `Pass` and `Preset`. */
export interface OfferBaselineSource extends ChannelSeedSource {
  settings: ChannelSeedSource['settings'] & { isNominative: boolean };
  bookingFormFields: Parameters<typeof mapHolderGroupFromBackend>[0];
  ticketHolderFormFields: Parameters<typeof mapHolderGroupFromBackend>[0];
}

// Shared by the live-form seeding effect and the edit-diff baseline so the two can never drift.
export const seedOfferFormFields = (
  offer: OfferBaselineSource | null | undefined,
  bookingPresets: FormFieldPreset[],
  defaultIsNominative: boolean
): FormFieldsSectionValue => ({
  isNominative: offer?.settings.isNominative ?? defaultIsNominative,
  mainHolder: mapHolderGroupFromBackend(offer?.bookingFormFields, bookingPresets),
  ticketHolder: mapHolderGroupFromBackend(offer?.ticketHolderFormFields, bookingPresets, {
    seedAssignedByDefault: false,
  }),
});

// `channelLimit` and `formFields` are seeded into the live form asynchronously and never live in
// `defaultValues`; reconstruct them here or the diff marks both dirty on every edit. Mirrors `useAdmissionForm`.
export const buildOfferEditBaseline = ({
  defaultValues,
  initialData,
  channelTeam,
  offer,
  activeChannels,
  bookingPresets,
  defaultIsNominative,
}: {
  defaultValues: AdmissionFormValue;
  initialData: AdmissionFormValue | undefined;
  channelTeam: EventTeam | undefined;
  offer: OfferBaselineSource | null;
  activeChannels: ActiveChannelRequest | undefined;
  bookingPresets: FormFieldPreset[];
  defaultIsNominative: boolean;
}): AdmissionFormValue => {
  const baselineChannelLimit =
    defaultValues.distribution.channelLimit ??
    (channelTeam ? seedChannelLimit(channelTeam, offer, undefined, activeChannels) : undefined);
  return {
    ...defaultValues,
    distribution: { ...defaultValues.distribution, channelLimit: baselineChannelLimit },
    formFields: initialData
      ? defaultValues.formFields
      : seedOfferFormFields(offer, bookingPresets, defaultIsNominative),
  };
};
